import React, { forwardRef } from 'react';

import { cssMap } from '@atlaskit/css';
import SettingsIcon from '@atlaskit/icon/core/settings';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';

import { IconButton } from '../IconButton';
import { type SettingsProps } from './types';

const iconSpacingStyles = cssMap({
	space050: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
});

/**
 * __Settings__
 *
 * A settings button that can be passed into `AtlassianNavigation`'s `renderSettings` prop.
 *
 * - [Examples](https://atlassian.design/components/atlassian-navigation/examples#settings)
 * - [Code](https://atlassian.design/components/atlassian-navigation/code)
 *
 * @deprecated `@atlaskit/atlassian-navigation` is deprecated. Use `@atlaskit/navigation-system` instead.
 */
export const Settings: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<SettingsProps> & React.RefAttributes<any>
> = forwardRef((props: SettingsProps, ref: React.Ref<any>) => {
	const {
		component,
		href,
		id,
		isDisabled,
		isSelected,
		label,
		onBlur,
		onClick,
		onFocus,
		onMouseDown,
		onMouseEnter,
		onMouseLeave,
		onMouseUp,
		target,
		testId,
		tooltip,
		...rest
	} = props;

	return (
		<div role="listitem">
			<IconButton
				component={component}
				href={href}
				icon={
					<Flex xcss={iconSpacingStyles.space050}>
						<SettingsIcon
							color="currentColor"
							label={typeof tooltip === 'string' ? tooltip : 'Settings Icon'}
						/>
					</Flex>
				}
				id={id}
				isDisabled={isDisabled}
				isSelected={isSelected}
				label={label}
				onBlur={onBlur}
				onClick={onClick}
				onFocus={onFocus}
				onMouseDown={onMouseDown}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onMouseUp={onMouseUp}
				ref={ref}
				target={target}
				testId={testId}
				tooltip={tooltip}
				isTooltipAnnouncementDisabled
				{...rest}
			/>
		</div>
	);
});
